import React from 'react';

import { faImage, faLocationDot, faUser } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { parkingManagementMessages } from 'src/features/parkingManagement/utils/messages';
import payStationsMessages from 'src/features/payStations/utils/messages';
import { MEMBERSHIP_ROLES } from 'src/features/userManagement/types';
import { useParking } from 'src/providers/ParkingProvider';
import { toast } from 'src/registry/hooks/UseToast';
import { Image } from 'src/registry/ui/Image';
import { generalMessages } from 'src/utils/messages';
import { storage } from 'src/utils/storage';

import type { Parking } from 'src/features/parkingManagement/types/parking';

type ParkingCardProps = {
    parking: Parking;
    role: MEMBERSHIP_ROLES;
};

export function ParkingCard({ parking, role }: ParkingCardProps) {
    const { formatMessage } = useIntl();
    const locale = storage.getLanguage();

    const { setParkingId, setParkingName, setParkingRole, setParkingCurrency } = useParking();

    const handleParkingChange = (parking: Parking, role: MEMBERSHIP_ROLES) => {
        setParkingId(parking.id);
        setParkingName(parking.name);
        setParkingRole(role);
        setParkingCurrency(parking.default_currency);
        toast({
            title: formatMessage(parkingManagementMessages.currentParkingChangeSuccess),
            description: (
                <FormattedMessage
                    {...parkingManagementMessages.currentParkingChangeSuccessDetails}
                    values={{
                        parkingName: parking.name,
                        b: (chunks: React.ReactNode[]) => <b className="font-semibold">{chunks}</b>,
                    }}
                />
            ),
        });
    };

    return (
        <Link
            to="/"
            onClick={() => handleParkingChange(parking, role)}
            className="group relative flex flex-col gap-4 rounded-lg border p-4 shadow-xs transition-colors hover:bg-gray-100 sm:flex-row"
        >
            <div className="flex flex-1 flex-row gap-4">
                {parking.photo ? (
                    <div className="relative aspect-video h-24 overflow-hidden rounded-lg">
                        <Image
                            image={parking.photo}
                            className="size-full object-cover"
                            alt={formatMessage(payStationsMessages.parking)}
                        />
                    </div>
                ) : (
                    <div className="flex aspect-video h-24 items-center justify-center rounded-lg border bg-gray-100">
                        <FontAwesomeIcon icon={faImage} size="2xl" className="text-gray-400" />
                    </div>
                )}
                <div className="flex flex-col gap-2">
                    <div className="flex w-full gap-2">
                        <div>
                            <p className="text-primary-700 text-sm">
                                {`${formatMessage(generalMessages.created)} ${new Date(
                                    parking.created_at,
                                ).toLocaleDateString(locale, {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                })}`}
                            </p>
                            <h2 className="text-lg font-medium">{parking.name}</h2>
                        </div>
                    </div>

                    <div className="flex flex-col gap-x-4 text-sm">
                        <div className="flex items-center gap-2">
                            <FontAwesomeIcon className="text-gray-500" icon={faLocationDot} />
                            <p>{parking.address}</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <FontAwesomeIcon className="text-gray-500" icon={faUser} />
                            <p>
                                {parking.users_count} {formatMessage(parkingManagementMessages.users)}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </Link>
    );
}
